import { createFileRoute, Link, useLocation, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, CircleAlert, Loader2, Mail, UserPlus } from "lucide-react";
import { useEffect, useRef } from "react";
import { z } from "zod";
import { useAcceptTeamInvite, useCurrentUser, useTeamInvitePreview } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { AuthLayout } from "@/features/auth/auth-layout";
import { extractApiError } from "@/lib/extract-api-error";
import { useAuthUIStore } from "@/stores/auth-ui";

const acceptInviteSearchSchema = z.object({
  token: z.string().catch(""),
});

export const Route = createFileRoute("/team-invites/accept")({
  validateSearch: acceptInviteSearchSchema,
  component: TeamInviteAcceptPage,
});

export function TeamInviteAcceptPage() {
  const { token } = Route.useSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const setRedirect = useAuthUIStore((state) => state.setRedirect);
  const currentUser = useCurrentUser();
  const preview = useTeamInvitePreview(token);
  const acceptInvite = useAcceptTeamInvite(token);
  const attemptedAcceptRef = useRef(false);

  const returnPath = `${location.pathname}${location.searchStr}`;

  useEffect(() => {
    if (!currentUser.data || !preview.data || preview.data.status !== "pending") return;
    if (attemptedAcceptRef.current || acceptInvite.isPending || acceptInvite.isSuccess) return;

    attemptedAcceptRef.current = true;
    acceptInvite.mutate(undefined, {
      onSuccess: () => {
        void navigate({ to: "/dashboard/calendar" });
      },
    });
  }, [acceptInvite, currentUser.data, navigate, preview.data]);

  const rememberReturnPath = () => setRedirect(returnPath);

  if (!token) {
    return (
      <InviteShell
        title="Invitation link missing"
        subtitle="Ask the business owner to send a new team invitation."
        icon={<CircleAlert className="size-5 text-danger" />}
      />
    );
  }

  if (preview.isLoading || currentUser.isLoading) {
    return (
      <InviteShell
        title="Checking invitation"
        subtitle="This takes just a moment."
        icon={<Loader2 className="size-5 animate-spin text-electric" />}
      />
    );
  }

  if (preview.isError) {
    return (
      <InviteShell
        title="Invitation unavailable"
        subtitle={extractApiError(
          preview.error,
          "Could not load this invitation. Reload the page to try again.",
        )}
        icon={<CircleAlert className="size-5 text-danger" />}
      />
    );
  }

  const invite = preview.data;
  if (!invite) {
    return (
      <InviteShell
        title="Invitation unavailable"
        subtitle="This invitation link is not valid anymore."
        icon={<CircleAlert className="size-5 text-danger" />}
      />
    );
  }

  if (invite.status !== "pending") {
    return (
      <InviteShell
        title="Invitation unavailable"
        subtitle={`This invitation is ${invite.status}. Ask ${invite.business.name} to send a new one.`}
        icon={<CircleAlert className="size-5 text-danger" />}
      />
    );
  }

  if (!currentUser.data) {
    return (
      <InviteShell
        title={`Join ${invite.business.name}`}
        subtitle={`Use ${invite.email} to sign in or create an account, then your ${invite.role} profile will be connected automatically.`}
        icon={<Mail className="size-5 text-electric" />}
      >
        <div className="grid gap-3">
          <Button asChild size="lg" className="w-full" onClick={rememberReturnPath}>
            <Link to="/auth/sign-in">Sign in to accept</Link>
          </Button>
          <Button
            asChild
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={rememberReturnPath}
          >
            <Link to="/auth/sign-up">Create account</Link>
          </Button>
        </div>
      </InviteShell>
    );
  }

  if (acceptInvite.isError) {
    return (
      <InviteShell
        title="Could not accept invitation"
        subtitle={extractApiError(acceptInvite.error, "The invitation could not be accepted.")}
        icon={<CircleAlert className="size-5 text-danger" />}
      >
        <Button asChild variant="secondary" className="w-full">
          <Link to="/account">Go to account</Link>
        </Button>
      </InviteShell>
    );
  }

  return (
    <InviteShell
      title={acceptInvite.isSuccess ? "Team profile connected" : `Joining ${invite.business.name}`}
      subtitle={
        acceptInvite.isSuccess
          ? "Opening your dashboard."
          : "Your account is being connected to this team profile."
      }
      icon={
        acceptInvite.isSuccess ? (
          <CheckCircle2 className="size-5 text-electric" />
        ) : (
          <Loader2 className="size-5 animate-spin text-electric" />
        )
      }
    />
  );
}

interface InviteShellProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  children?: React.ReactNode;
}

function InviteShell({ title, subtitle, icon, children }: InviteShellProps) {
  return (
    <AuthLayout title={title} subtitle={subtitle}>
      <div className="space-y-5">
        <div className="mx-auto flex size-11 items-center justify-center rounded-full bg-electric-50">
          {icon}
        </div>
        {children}
        {!children && (
          <div className="flex justify-center">
            <Button asChild variant="secondary">
              <Link to="/">
                <UserPlus className="size-4" />
                Back to Slotique
              </Link>
            </Button>
          </div>
        )}
        <Text variant="small" className="text-center text-slate">
          Team access is granted only after you accept from the invited email address.
        </Text>
      </div>
    </AuthLayout>
  );
}
